import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, ChevronRight, Inbox } from 'lucide-react';
import Shell from '../../components/TopBarShell';
import { useReviewQueue } from '../../contexts/ReviewQueueContext';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import { deptLabel } from '../../lib/departments';
import api from '../../utils/api';

const fmtMoney = (n) => `€ ${Number(n || 0).toFixed(2)}`;

const ReviewQueue = () => {
  const { count: reviewCount, refresh } = useReviewQueue();
  const t = useT();
  const { locale, intlLocale } = useLocale();
  const fmtDate = (iso) => iso ? new Intl.DateTimeFormat(intlLocale).format(new Date(iso)) : '·';
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.toReview().then(setReports).catch(() => setReports([]));
  }, []);

  return (
    <Shell reviewCount={reviewCount}>
      <header className="mb-6">
        <div className="flex items-center gap-2 text-[hsl(var(--text-soft))] text-[14px] mb-1.5">
          <CheckSquare className="h-4 w-4" /> {t('review.asManager')}
        </div>
        <h1 className="font-display text-[1.75rem]">{t('review.title')}</h1>
        <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">
          {t('review.intro')}
        </p>
      </header>

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
        {reports === null && (
          <div className="px-5 py-12 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</div>
        )}
        {reports !== null && reports.length === 0 && (
          <div className="px-5 py-14 text-center">
            <Inbox className="h-7 w-7 mx-auto text-[hsl(var(--text-muted))]" />
            <div className="mt-3 text-sm font-medium">{t('review.empty')}</div>
            <p className="text-xs text-[hsl(var(--text-soft))] mt-1">
              {t('review.empty.hint')}
            </p>
          </div>
        )}
        {reports !== null && reports.length > 0 && (
          <>
            <div className="px-5 py-3 grid grid-cols-12 gap-4 text-[13px] text-[hsl(var(--text-muted))] border-b border-border bg-[hsl(var(--bg-subtle))]">
              <div className="col-span-4">{t('review.col.collab')}</div>
              <div className="col-span-4">{t('review.col.note')}</div>
              <div className="col-span-2">{t('review.col.submitted')}</div>
              <div className="col-span-2 text-right">{t('review.col.amount')}</div>
            </div>
            <ul className="divide-y divide-[hsl(var(--rule))]">
              {reports.map(r => (
                <li key={r.id}>
                  {/* Status badge dropped · every row in this queue is by
                       definition "awaiting manager validation", so the pill
                       was redundant and was crushing the amount column. */}
                  <Link to={`/review/${r.id}`} onMouseEnter={refresh} className="px-5 py-3 grid grid-cols-12 gap-4 items-center hover:bg-[hsl(var(--surface-2))]">
                    <div className="col-span-4 min-w-0">
                      <div className="text-sm truncate">{r.submitter_full_name || r.submitter_email}</div>
                      <div className="text-[11px] text-[hsl(var(--text-muted))]">{r.submitter_departement ? deptLabel(r.submitter_departement, locale) : '·'}</div>
                    </div>
                    <div className="col-span-4 min-w-0">
                      <div className="text-sm truncate">{r.title || t('expense.noteOf', { date: fmtDate(r.created_at) })}</div>
                      <div className="text-[11px] text-[hsl(var(--text-muted))] font-num">NDF-{String(r.id).padStart(5, '0')}</div>
                    </div>
                    <div className="col-span-2 text-sm text-[hsl(var(--text-soft))]">{fmtDate(r.submitted_at)}</div>
                    <div className="col-span-2 text-sm font-num text-right flex items-center justify-end gap-2 whitespace-nowrap">
                      {fmtMoney(r.total_tvac)}
                      <ChevronRight className="h-4 w-4 text-[hsl(var(--text-muted))] shrink-0" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Shell>
  );
};

export default ReviewQueue;
